import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Mail, FileText, CreditCard, User, ArrowRight, ArrowLeft } from 'lucide-react';

interface SubscriptionData {
  plan: {
    id: string;
    name: string;
    price: number;
    description: string;
  };
  customerInfo: {
    companyName: string;
    contactName: string;
    email: string;
    phone: string;
  };
}

const SubscriptionConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);

  useEffect(() => {
    const data = sessionStorage.getItem('subscriptionData');
    if (data) {
      setSubscriptionData(JSON.parse(data));
    } else {
      navigate('/subscription');
    }
  }, [navigate]);

  if (!subscriptionData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-50 to-slate-100 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-5xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent mb-2">
            Grid Managed Services
          </h1>
          <p className="text-gray-600">Subscription Confirmation</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-8 py-6">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => navigate('/')}
                className="btn btn-secondary btn-sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </button>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <span className="text-sm font-semibold text-green-600">Request Received</span>
              </div>
            </div>
          </div>

          {/* Card Content */}
          <div className="p-8 md:p-12">
            {/* Success Message */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Thank You for Choosing Grid Managed Services!
              </h2>
              <p className="text-lg text-gray-600">
                Your subscription request has been successfully received
              </p>
            </div>

            <div 
              className="max-w-4xl mx-auto"
              style={{
                border: '3px solid #d1d5db',
                backgroundColor: '#f9fafb',
                borderRadius: '16px',
                padding: '3rem'
              }}
            >
              {/* Email Notification Card */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-blue-900 mb-2">Check Your Email</h3>
                    <p className="text-blue-800 mb-3">
                      We've sent a comprehensive email to <span className="font-bold">{subscriptionData.customerInfo.email}</span> containing:
                    </p>
                  </div>
                </div>
                <table className="w-full">
                  <tbody>
                    <tr>
                      <td className="align-top pr-3 pb-2" style={{ width: '20px' }}>
                        <FileText className="w-5 h-5 text-blue-800 flex-shrink-0" />
                      </td>
                      <td className="align-top pb-2">
                        <span className="text-blue-800 text-sm">Service contract for your review and digital signature</span>
                      </td>
                    </tr>
                    <tr>
                      <td className="align-top pr-3 pb-2" style={{ width: '20px' }}>
                        <CreditCard className="w-5 h-5 text-blue-800 flex-shrink-0" />
                      </td>
                      <td className="align-top pb-2">
                        <span className="text-blue-800 text-sm">Secure payment setup instructions</span>
                      </td>
                    </tr>
                    <tr>
                      <td className="align-top pr-3" style={{ width: '20px' }}>
                        <User className="w-5 h-5 text-blue-800 flex-shrink-0" />
                      </td>
                      <td className="align-top">
                        <span className="text-blue-800 text-sm">Sign in credentials and portal access information</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Subscription Summary Card */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Your Subscription Summary</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Selected Plan</p>
                    <p className="text-lg font-bold text-gray-900">{subscriptionData.plan.name}</p>
                    <p className="text-gray-600 text-sm">{subscriptionData.plan.description}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Monthly Investment</p>
                    <p className="text-3xl font-bold text-red-600">${subscriptionData.plan.price}<span className="text-lg text-gray-600">/month</span></p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Company</p>
                    <p className="font-bold text-gray-900">{subscriptionData.customerInfo.companyName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Primary Contact</p>
                    <p className="font-bold text-gray-900">{subscriptionData.customerInfo.contactName}</p>
                  </div>
                </div>
              </div>

              {/* Next Steps Card */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-green-900 mb-4">Complete These Steps</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-green-900 mb-1">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-green-600 text-white rounded-full text-xs font-bold mr-2">1</span>
                      <span className="mr-2">-</span>
                      Check your email inbox
                    </p>
                    <p className="text-green-800 text-sm ml-8 pl-2">Look for an email from Grid Managed Services</p>
                  </div>
                  <div>
                    <p className="font-semibold text-green-900 mb-1">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-green-600 text-white rounded-full text-xs font-bold mr-2">2</span>
                      <span className="mr-2">-</span>
                      Review and sign the contract
                    </p>
                    <p className="text-green-800 text-sm ml-8 pl-2">Click the link in the email to digitally sign your service agreement</p>
                  </div>
                  <div>
                    <p className="font-semibold text-green-900 mb-1">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-green-600 text-white rounded-full text-xs font-bold mr-2">3</span>
                      <span className="mr-2">-</span>
                      Set up payment
                    </p>
                    <p className="text-green-800 text-sm ml-8 pl-2">Complete the secure payment setup to activate your service</p>
                  </div>
                  <div>
                    <p className="font-semibold text-green-900 mb-1">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-green-600 text-white rounded-full text-xs font-bold mr-2">4</span>
                      <span className="mr-2">-</span>
                      Access your portal
                    </p>
                    <p className="text-green-800 text-sm ml-8 pl-2">Use your sign in credentials to access the customer portal</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-between items-center w-full">
          <button 
            onClick={() => navigate('/')}
            className="btn btn-secondary btn-lg"
          >
            Return to Home
          </button>
          <button 
            onClick={() => navigate('/dashboard')}
            className="btn btn-primary btn-lg"
          >
            Go to Portal
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>

        {/* Support Note */}
        <div className="text-center mt-8">
          <p className="text-gray-600 text-sm">
            Need help? Contact us at <a href="mailto:info@gridmanagedservices.com" className="text-red-600 hover:text-red-700 font-semibold">info@gridmanagedservices.com</a> or call <a href="tel:5551234357" className="text-red-600 hover:text-red-700 font-semibold">(555) 123-HELP</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionConfirmation;
